import { createSlice } from '@reduxjs/toolkit'
import {
  reIndexByMatch,
  reIndexOnDelete,
  multiSectionFilter,
  orderByPosition,
} from 'apps/builder/store/helpers'
import { matchById } from 'apps/builder/lib/utils'
import {
  addSheet,
  addTableSheet,
  addListSheet,
  updateSheet,
  updateSheetSection,
  copyTableOrSimpleLoopSheet,
  deleteSheet,
  deleteChildSheet,
  copySheet,
  splitSheet,
  deleteLoopSheet,
  convertToLoopScreen,
  promoteToLoopScreen,
  convertToTableScreen,
  promoteToTableScreen,
  importFormData,
} from 'apps/formsCreationKit/store/actions/sharedActions'

import { saveApp } from 'apps/formsCreationKit/store/reducers/settings/appSettings'

import { lib, actions as clientActions } from '@gocanvas/client'
const {
  NORMAL_SECTION_TYPE_ID, // 10
  MULTIPLE_ENTRY_SECTION_TYPE_ID, // 11
} = lib
const { loadFormData } = clientActions

const extractOrderedSectionGroups = sections => {
  const topLevelSections = sections
    .filter(multiSectionFilter)
    .sort(orderByPosition)
    .map((section, i) => ({ ...section, position: i }))

  const nestedSections = sections.filter(
    section => !multiSectionFilter(section)
  )

  return { topLevelSections, nestedSections }
}

const reorderTopLevelSections = (sections, id, newPosition, oldPosition) => {
  const { topLevelSections, nestedSections } =
    extractOrderedSectionGroups(sections)
  // accounts for indexes over the current # of sections, e.g. extra drop targets
  const maxPosition = Math.min(newPosition, topLevelSections.length - 1)
  const updatedTopLevelSections = reIndexByMatch(
    topLevelSections,
    matchById(id),
    maxPosition,
    oldPosition
  )

  return [...updatedTopLevelSections, ...nestedSections].map((section, i) => ({
    ...section,
    position: i,
  }))
}

// translates a screen index position into actual section index by skipping multisheets
const calculateInsertIndex = (sections, targetPosition) => {
  let index = 0
  let screenCounter = 0

  // "skips" section_type_id 11 sections, as they don't count towards the top-level "screen" count
  while (screenCounter < targetPosition && index < sections.length) {
    while (
      index < sections.length &&
      sections[index].section_type_id === MULTIPLE_ENTRY_SECTION_TYPE_ID
    ) {
      index++
    }

    // extra check to ensure we don't ever exceed the sections array length
    if (index < sections.length) {
      // Move to the next section and increment the screen counter
      index++
      screenCounter++
    }
  }

  return index
}

const convertToLoopSection = (state, action) => {
  const { newSectionId, section_id, parentSheetId, newSheetDescription } =
    action.payload

  const sectionIndex = state.findIndex(matchById(section_id))
  const section = state[sectionIndex]

  // original section, which will hold the new keyfield sheet, depth/position is unchanged
  const oldSection = {
    ...section,
    description: `${newSheetDescription}`,
    section_type_id: parentSheetId
      ? MULTIPLE_ENTRY_SECTION_TYPE_ID
      : NORMAL_SECTION_TYPE_ID,
  }

  // new "detail" section, which will hold the old sheet
  const newSection = {
    id: newSectionId,
    section_type_id: MULTIPLE_ENTRY_SECTION_TYPE_ID,
    description: `${newSheetDescription} (Detail)`,
    depth: section.depth + 1,
    hides_detailed_description: true,
    position: sectionIndex + 1, // just as long as it's not 0
  }

  return [
    ...state.slice(0, sectionIndex),
    oldSection,
    newSection,
    // position + 1 filters out the original section we replaced with oldSection
    ...state.slice(sectionIndex + 1).map(section => {
      return {
        ...section,
        position: section.position + 1,
      }
    }),
  ]
}

const processCopyOrSplitSheet = (state, action) => {
  const { sectionId, newSectionId, parentSheet } = action.payload
  const actionType = action.type

  if (parentSheet) {
    // Sheet is added to the existing section when a parent sheet exists; do nothing
    return state
  } else {
    const originalSection = state.find(matchById(sectionId))
    const copiedSection = {
      ...originalSection,
      id: newSectionId,
      description: `${originalSection.description || ''} - ${
        copySheet.type === actionType ? 'copy' : 'split'
      }`,
      position: originalSection.position + 1,
      depth: 0,
    }

    return [...state, copiedSection]
      .map(section => {
        if (
          section.id !== copiedSection.id &&
          section.position >= copiedSection.position
        ) {
          return {
            ...section,
            position: section.position + 1,
          }
        }
        return section
      })
      .sort(orderByPosition)
  }
}

const createTableSections = (state, action) => {
  const { section_id, multiSectionGuid, sectionPosition } = action.payload
  const nonMultiSection = state.filter(multiSectionFilter)
  const newSectionName = `New Screen ${nonMultiSection.length + 1}`
  const position = sectionPosition
    ? calculateInsertIndex(state, sectionPosition)
    : state.length

  const newSections = [
    {
      id: section_id,
      section_type_id: NORMAL_SECTION_TYPE_ID,
      description: newSectionName,
      placeholder: newSectionName,
      position: position,
      depth: 0,
    },
  ]

  if (multiSectionGuid) {
    // Multi-section is only created for Table sheets, not List sheets
    newSections.push({
      id: multiSectionGuid,
      section_type_id: MULTIPLE_ENTRY_SECTION_TYPE_ID,
      description: `${newSectionName} (Detail)`,
      placeholder: newSectionName,
      hides_detailed_description: true,
      position: position + 1,
      depth: 1,
    })
  }

  return [
    ...state.slice(0, position),
    ...newSections,
    ...state.slice(position).map(section => {
      return {
        ...section,
        position: section.position + 2,
      }
    }),
  ]
}

export const initialState = []

export const sectionsSliceExtraReducers = builder => {
  builder
    .addCase(addSheet, (state, action) => {
      const { section_id, sectionPosition } = action.payload
      const nonMultiSection = state.filter(multiSectionFilter)
      const newScreenName = `New Screen ${nonMultiSection.length + 1}`
      const position = sectionPosition
        ? calculateInsertIndex(state, sectionPosition)
        : state.length

      const newSection = {
        id: section_id,
        description: newScreenName,
        placeholder: newScreenName,
        position,
        section_type_id: NORMAL_SECTION_TYPE_ID,
        depth: 0,
      }
      return [
        ...state.slice(0, position),
        newSection,
        ...state.slice(position).map(section => {
          return {
            ...section,
            position: section.position + 1,
          }
        }),
      ]
    })
    .addCase(updateSheet, (state, action) => {
      const { section_id, description, placeholder, multi_section } =
        action.payload
      return state.map(entry => {
        if (multi_section === entry.id) {
          return {
            ...entry,
            description: `${description} (Detail)`,
            placeholder,
          }
        }
        if (section_id === entry.id) {
          return {
            ...entry,
            description,
            placeholder,
          }
        }

        return entry
      })
    })
    .addCase(updateSheetSection, (state, action) => {
      const {
        currentSectionId, // section where the sheet currently exists
        deleteSection,
        section_id, //id of a section that already exists, or to create
        position,
        description,
        childSheets,
      } = action.payload

      const childSections = childSheets.map(sheet => `${sheet.section_id}`)

      const currentSection = state.find(
        section => section.id === currentSectionId
      )
      const dropSection = state.find(section => section.id === section_id)

      const depthDifference = dropSection
        ? currentSection.depth - dropSection.depth
        : currentSection.depth - 0

      let newState = state

      // if a sheet came from the top-level, or else if it was the the
      // last in its section, then the source section is deleted
      if (deleteSection && currentSection) {
        newState = state.filter(
          section => ![currentSectionId].includes(section.id)
        )
        newState = reIndexOnDelete(newState, newState.length)
      }

      // if a sheet was moved into the top-level then a new section
      // needs to be created to hold its top-level position
      if (section_id && !dropSection) {
        const nonMultiSection = state.filter(multiSectionFilter)
        const newSection = {
          id: section_id,
          description,
          placeholder: `New Screen ${state.length}`,
          position: position,
          section_type_id: NORMAL_SECTION_TYPE_ID,
          depth: 0,
        }
        newState = reorderTopLevelSections(
          [...newState, newSection],
          section_id,
          newSection.position,
          nonMultiSection.length
        )
      }
      return newState.map(section => {
        if (childSections.includes(`${section.id}`)) {
          return {
            ...section,
            depth: section.depth - depthDifference,
          }
        }
        return section
      })
    })
    .addCase(deleteSheet, (state, action) => {
      const { sectionId, additionalSheets = [] } = action.payload
      const topLevelSection = state.find(section => section.id === sectionId)
      const sectionsToDelete = [
        sectionId,
        ...additionalSheets.map(({ section_id }) => section_id),
      ]
      const newState = state.filter(
        section => !sectionsToDelete.includes(section.id)
      )

      return reIndexOnDelete(newState, topLevelSection.position)
    })
    .addCase(deleteChildSheet, (state, action) => {
      const {
        sectionId,
        deleteTopLevelSection,
        additionalSheets = [],
      } = action.payload
      const topLevelSection = state.find(section => section.id === sectionId)
      const sectionsToDelete = additionalSheets.map(
        ({ section_id }) => section_id
      )
      if (deleteTopLevelSection) {
        sectionsToDelete.push(sectionId)
      }

      const newState = state.filter(
        section => !sectionsToDelete.includes(section.id)
      )

      return deleteTopLevelSection
        ? reIndexOnDelete(newState, topLevelSection.position)
        : newState
    })
    .addCase(deleteLoopSheet, (state, action) => {
      const { sectionId, directDescendants, parentSheet } = action.payload
      const multiSectionId = directDescendants[0].section_id
      const sectionsToDelete = parentSheet
        ? [multiSectionId]
        : [sectionId, multiSectionId]
      const topLevelSection = state.find(section => section.id === sectionId)

      // When a parent sheet exists, we preserve the loop sheet's section and move the child sheets to
      // that section so no repositioning of sections is needed.
      // When a parent sheet does not exist, we delete the loop sheet's section and create new sections
      // for each child sheet, which become top-level sections and a repostioning of sections is needed.
      const newState = state.reduce((acc, section) => {
        if (!parentSheet && section.id === sectionId) {
          const newSections = directDescendants.map((sheet, index) => ({
            id: sheet.newSectionId,
            description: sheet.description,
            section_type_id: NORMAL_SECTION_TYPE_ID,
            position: topLevelSection.position + index,
            depth: 0,
          }))
          acc.push(...newSections)
        }

        if (!sectionsToDelete.includes(section.id)) {
          acc.push({
            ...section,
            position:
              !parentSheet && section.position > topLevelSection.position
                ? section.position + directDescendants.length - 1
                : section.position,
          })
        }

        return acc
      }, [])

      return newState
    })
    .addCase(copySheet, processCopyOrSplitSheet)
    .addCase(splitSheet, processCopyOrSplitSheet)
    .addCase(addTableSheet, createTableSections)
    .addCase(addListSheet, createTableSections)
    .addCase(copyTableOrSimpleLoopSheet, (state, action) => {
      const { sectionsToCopy, newSectionGuid, newMultiSectionGuid } =
        action.payload
      const originalSections = state.filter(({ id }) =>
        sectionsToCopy.includes(id)
      )

      const newSections = [...originalSections].map(section => {
        const isMultiSection =
          section.section_type_id === MULTIPLE_ENTRY_SECTION_TYPE_ID
        return {
          ...section,
          description: isMultiSection
            ? `New Screen ${state.length} (Detail)`
            : `New Screen ${state.length}`,
          id: isMultiSection ? newMultiSectionGuid : newSectionGuid,
          position: section.position + originalSections.length,
          depth: isMultiSection ? 1 : 0,
        }
      })

      const firstNewSectionPosition = Math.min(
        ...newSections.map(({ position }) => position)
      )

      return [...state, ...newSections]
        .map(section => {
          if (
            ![newSectionGuid, newMultiSectionGuid].includes(section.id) &&
            section.position >= firstNewSectionPosition
          ) {
            return {
              ...section,
              position: section.position + originalSections.length,
            }
          }
          return section
        })
        .sort(orderByPosition)
    })
    .addCase(convertToLoopScreen, convertToLoopSection)
    .addCase(promoteToLoopScreen, convertToLoopSection)
    .addCase(convertToTableScreen, convertToLoopSection)
    .addCase(promoteToTableScreen, convertToLoopSection)
    .addCase(loadFormData, (_, action) => {
      const { sections } = action.payload
      return sections
    })
    .addCase(importFormData, (state, action) => {
      const { sections } = action.payload
      return [...state, ...sections]
    })
    .addCase(saveApp.fulfilled, (state, action) => {
      const { entities } = action.payload
      const { sections } = entities
      return sections
    })
}

export const sectionsSliceConfig = {
  name: 'sections',
  initialState: initialState,
  reducers: {
    updateIndex(state, action) {
      const { id, newPosition, oldPosition } = action.payload
      return reorderTopLevelSections(state, id, newPosition, oldPosition)
    },
  },
  extraReducers: sectionsSliceExtraReducers,
}

export const sectionsSlice = createSlice(sectionsSliceConfig)
export const { actions: sectionsActions, reducer: sectionsReducer } =
  sectionsSlice
